/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useMemo } from 'react'
import { useIntl, defineMessages } from 'react-intl'

import {
  EuiBadge,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiIcon,
  EuiPanel,
  EuiSkeletonText,
  EuiSpacer,
  EuiSuperSelect,
  EuiSwitch,
  EuiText,
  EuiTitle,
} from '@elastic/eui'
import type { EuiSuperSelectOption } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type {
  DeploymentTemplateInfoV2,
  DeploymentUpdateRequest,
  InstanceConfiguration,
  ElasticsearchClusterTopologyElement,
} from '@modules/cloud-api/v1/types'

import { getTier, type DataTier } from '@/lib/stackDeployments/selectors/nodeRoles'

import {
  createZoneSummaryInstance,
  filterInstanceConfigurationsForTier,
  getTemplateInstanceConfigurationsByTier,
  type TierInstanceConfigurations,
} from './tierInstanceConfigHelpers'
import ChangeSummary from './ChangeSummary'
import { getDiffs } from './hardwareComparisonHelpers'
import HardwareSummary from './HardwareSummary'

import type { MessageDescriptor } from 'react-intl'

const messages = defineMessages({
  tierConfigTitle: {
    id: 'deployment-template.tier-config.title',
    defaultMessage: 'Tier-specific instance configurations',
  },
  tierConfigDescription: {
    id: 'deployment-template.tier-config.description',
    defaultMessage: 'Choose an instance configuration for each data tier',
  },
  hotTierLabel: {
    id: 'deployment-template.tier-config.hot-tier',
    defaultMessage: 'Hot tier',
  },
  warmTierLabel: {
    id: 'deployment-template.tier-config.warm-tier',
    defaultMessage: 'Warm tier',
  },
  coldTierLabel: {
    id: 'deployment-template.tier-config.cold-tier',
    defaultMessage: 'Cold tier',
  },
  frozenTierLabel: {
    id: 'deployment-template.tier-config.frozen-tier',
    defaultMessage: 'Frozen tier',
  },
  newConfigLabel: {
    id: 'deployment-template.tier-config.new-config',
    defaultMessage: 'New configuration (per zone)',
  },
  keepSameConfig: {
    id: 'deployment-template.tier-config.keep-same',
    defaultMessage: 'Keep the same configuration',
  },
  showDetailsToggle: {
    id: 'deployment-template.tier-config.show-breakdown',
    defaultMessage: 'Show breakdown',
  },
})

interface TierConfig {
  tier: DataTier
  label: MessageDescriptor
}

interface Props {
  selectedTemplate: DeploymentTemplateInfoV2 | undefined
  instanceConfigurations: InstanceConfiguration[]
  deploymentInstanceConfigurations?: InstanceConfiguration[]
  esTopology: ElasticsearchClusterTopologyElement[]
  migratedTemplatePayload: DeploymentUpdateRequest | null
  tierConfigurations: TierInstanceConfigurations
  onTierConfigurationChange: (tier: DataTier, instanceConfigurationId: string) => void
  allowLegacyICs?: boolean
  showDetails: boolean
  onShowDetailsChange: (showDetails: boolean) => void
  isLoading?: boolean
}

const TIER_CONFIGS: TierConfig[] = [
  { tier: 'hot', label: messages.hotTierLabel },
  { tier: 'warm', label: messages.warmTierLabel },
  { tier: 'cold', label: messages.coldTierLabel },
  { tier: 'frozen', label: messages.frozenTierLabel },
]

const TierInstanceConfigurationSelect: React.FunctionComponent<Props> = ({
  selectedTemplate,
  instanceConfigurations,
  deploymentInstanceConfigurations,
  esTopology,
  migratedTemplatePayload,
  tierConfigurations,
  onTierConfigurationChange,
  allowLegacyICs = false,
  showDetails,
  onShowDetailsChange,
  isLoading = false,
}) => {
  const { formatMessage } = useIntl()

  const templateConfigs = useMemo(
    () => (selectedTemplate ? getTemplateInstanceConfigurationsByTier(selectedTemplate) : {}),
    [selectedTemplate],
  )

  const templateTopology = useMemo(
    () => migratedTemplatePayload?.resources?.elasticsearch?.[0]?.plan?.cluster_topology || [],
    [migratedTemplatePayload],
  )

  const getTierTopologyElement = (
    tier: DataTier,
    topology: ElasticsearchClusterTopologyElement[],
  ): ElasticsearchClusterTopologyElement | undefined =>
    topology.find((element) => getTier(element) === tier)

  const getTemplateDefaultIC = (tier: DataTier): string | undefined => templateConfigs[tier]

  const getCurrentDeploymentIC = (tier: DataTier) => {
    const topologyElement = getTierTopologyElement(tier, esTopology)
    return deploymentInstanceConfigurations?.find(
      (ic: InstanceConfiguration) => ic.id === topologyElement?.instance_configuration_id,
    )
  }

  const TierRow = (tierConfig: TierConfig): JSX.Element | null => {
    const { tier, label } = tierConfig
    const topologyElement = getTierTopologyElement(tier, esTopology)

    if (!topologyElement) {
      return null
    }

    const isInactiveTier = !topologyElement.size?.value || topologyElement.size.value === 0

    if (isInactiveTier) {
      return null
    }

    const currentICObject = getCurrentDeploymentIC(tier)
    const currentIC = currentICObject?.id
    const templateDefaultIC = getTemplateDefaultIC(tier)
    const selectedIC = tierConfigurations[tier]

    const currentInstanceSummary = currentICObject
      ? createZoneSummaryInstance(currentICObject, topologyElement)
      : undefined

    const selectedICObject = instanceConfigurations.find(
      (ic: InstanceConfiguration) => ic.id === selectedIC,
    )
    const templateTopologyElement = getTierTopologyElement(tier, templateTopology)

    const selectedInstanceSummary =
      selectedICObject && templateTopologyElement
        ? createZoneSummaryInstance(selectedICObject, templateTopologyElement)
        : undefined

    const isExactMatch =
      currentIC === selectedIC &&
      currentICObject?.config_version === selectedICObject?.config_version

    const diffs =
      currentInstanceSummary && selectedInstanceSummary
        ? getDiffs(currentInstanceSummary, selectedInstanceSummary)
        : null

    const isThereDiff =
      diffs != null && (diffs.totalSizeDiff !== 0 || diffs.cpuDiff !== 0 || diffs.storageDiff !== 0)
    const availableICs = filterInstanceConfigurationsForTier({
      instanceConfigurations,
      tier,
      topologyElement,
      allowLegacy: allowLegacyICs,
      allowedICIds: templateDefaultIC ? [templateDefaultIC] : [],
    })

    const options: Array<EuiSuperSelectOption<string>> = []

    const currentVersion = currentICObject?.config_version

    const showVersionUpgrade =
      currentIC === selectedIC &&
      currentICObject?.config_version !== selectedICObject?.config_version

    // Always show "Keep the same configuration" option for the current deployment's IC
    if (currentIC) {
      options.push({
        value: currentIC,
        inputDisplay: formatMessage(messages.keepSameConfig),
        dropdownDisplay: (
          <Fragment>
            <strong>{formatMessage(messages.keepSameConfig)}</strong>
            <EuiText size='s' color='subdued'>
              {currentIC}
            </EuiText>
          </Fragment>
        ),
      })
    }

    for (const ic of availableICs) {
      if (ic.id === currentIC) {
        continue
      }

      if (ic.id) {
        const displayName = ic.name || ic.id
        const showIdSeparately = ic.name && ic.name !== ic.id

        options.push({
          value: ic.id,
          inputDisplay: (
            <Fragment>
              {displayName}{' '}
              {ic.config_version !== undefined && <EuiBadge>v{ic.config_version}</EuiBadge>}
            </Fragment>
          ),
          dropdownDisplay: (
            <Fragment>
              {displayName}{' '}
              {ic.config_version !== undefined && <EuiBadge>v{ic.config_version}</EuiBadge>}
              {showIdSeparately && (
                <EuiText size='s' color='subdued'>
                  {ic.id}
                </EuiText>
              )}
            </Fragment>
          ),
        })
      }
    }

    return (
      <EuiPanel
        key={tier}
        hasBorder={true}
        hasShadow={false}
        paddingSize='m'
        color='subdued'
        style={{ marginBottom: '8px' }}
        data-test-id={`tier-config-${tier}`}
      >
        <EuiFlexGroup gutterSize='m' justifyContent='spaceBetween' alignItems='flexStart'>
          <EuiFlexItem grow={false} style={{ minWidth: '280px' }}>
            <EuiText size='s'>
              <strong>
                <FormattedMessage {...label} />
              </strong>
            </EuiText>
            {!showDetails && (
              <Fragment>
                <EuiSpacer size='s' />
                <EuiText size='s' color='subdued'>
                  {currentIC} {` `}
                  {currentVersion !== undefined && <EuiBadge>v{currentVersion}</EuiBadge>}
                </EuiText>
              </Fragment>
            )}
            {showDetails && currentInstanceSummary && (
              <Fragment>
                <EuiSpacer size='s' />
                <HardwareSummary
                  moreSpace={showDetails}
                  instance={currentInstanceSummary}
                  topologyElement={topologyElement}
                />
              </Fragment>
            )}
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiFlexGroup alignItems='flexStart' gutterSize='m'>
              <EuiFlexItem grow={false}>
                <EuiIcon type='sortRight' />
              </EuiFlexItem>
              <EuiFlexItem grow={false} style={{ width: '400px' }}>
                <EuiFormRow
                  label={
                    <Fragment>
                      {formatMessage(messages.newConfigLabel)}
                      {showVersionUpgrade && (
                        <Fragment>
                          {' '}
                          <EuiBadge color='hollow'>v{currentVersion}</EuiBadge>{' '}
                          <EuiIcon type='sortRight' size='s' />{' '}
                          <EuiBadge color='primary'>v{selectedICObject?.config_version}</EuiBadge>
                        </Fragment>
                      )}
                    </Fragment>
                  }
                  fullWidth={true}
                >
                  <EuiSuperSelect
                    fullWidth={true}
                    options={options}
                    valueOfSelected={selectedIC || templateDefaultIC || ''}
                    onChange={(value) => onTierConfigurationChange(tier, value)}
                    hasDividers={true}
                  />
                </EuiFormRow>
                {showDetails && (
                  <Fragment>
                    <EuiSpacer size='s' />
                    <ChangeSummary
                      showDetails={true}
                      exact={isExactMatch && !isThereDiff}
                      partial={isExactMatch && !isThereDiff ? undefined : selectedInstanceSummary}
                      diffs={diffs}
                      topologyElement={topologyElement}
                    />
                  </Fragment>
                )}
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiPanel>
    )
  }

  return (
    <Fragment>
      <EuiFlexGroup alignItems='center' justifyContent='spaceBetween'>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup alignItems='center' gutterSize='s'>
            <EuiFlexItem grow={false}>
              <EuiTitle size='xxs'>
                <h4>
                  <FormattedMessage {...messages.tierConfigTitle} />
                </h4>
              </EuiTitle>
            </EuiFlexItem>
            <EuiFlexItem grow={false} />
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiSwitch
            label={formatMessage(messages.showDetailsToggle)}
            checked={showDetails}
            onChange={(e) => onShowDetailsChange(e.target.checked)}
            compressed={true}
          />
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiText size='xs' color='subdued' style={{ marginBottom: '16px' }}>
        <FormattedMessage {...messages.tierConfigDescription} />
      </EuiText>
      {isLoading ? (
        <Fragment>
          <EuiSkeletonText lines={3} />
          <EuiSpacer size='m' />
          <EuiSkeletonText lines={3} />
        </Fragment>
      ) : (
        TIER_CONFIGS.map(TierRow)
      )}
    </Fragment>
  )
}

export default TierInstanceConfigurationSelect
